<template>
  <HrPageLayout
    title="Appraisal Form Templates"
    subtitle="Upload PDF appraisal forms and assign them to employee groups"
    icon="📄"
    :breadcrumb-items="[{ label: 'Performance' }, { label: 'Appraisal Forms' }]"
  >
    <template #header-actions>
      <button class="hr-btn hr-btn-primary" @click="openCreate">
        <i class="bx bx-upload"></i> Upload Form
      </button>
    </template>

    <div v-if="loading" class="hr-loading-wrap hr-py-5">
      <div class="hr-spinner"></div>
    </div>

    <div v-else class="hr-chart-card">
      <div class="hr-chart-body">
        <div class="hr-table-wrapper responsive-table-wrap">
          <table class="hr-table hr-table-hover">
            <thead>
              <tr>
                <th>Title</th>
                <th>Period</th>
                <th>Employee Group</th>
                <th>Deadline</th>
                <th>Status</th>
                <th class="hr-text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="form in forms" :key="form.id">
                <td>
                  <div class="hr-fw-medium">{{ form.title }}</div>
                  <small class="hr-text-muted">{{ form.pdf_original_name }}</small>
                </td>
                <td>{{ form.appraisal_year || '—' }} {{ form.period_label ? `· ${form.period_label}` : '' }}</td>
                <td>{{ groupLabel(form) }}</td>
                <td>{{ form.submission_deadline || 'No deadline' }}</td>
                <td>
                  <span class="hr-badge" :class="form.is_active == 1 ? 'hr-badge-success' : 'hr-badge-secondary'">
                    {{ form.is_active == 1 ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <td class="hr-text-end">
                  <div class="hr-action-btns">
                    <button class="hr-btn hr-btn-sm hr-btn-info" @click="downloadTemplate(form.id)" title="Download PDF">
                      <i class="bx bx-download"></i>
                    </button>
                    <button class="hr-btn hr-btn-sm hr-btn-primary" @click="openEdit(form)" title="Edit">
                      <i class="bx bx-edit"></i>
                    </button>
                    <button class="hr-btn hr-btn-sm hr-btn-danger" @click="deactivate(form)" title="Deactivate">
                      <i class="bx bx-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="forms.length === 0">
                <td colspan="6" class="hr-text-center hr-py-4 hr-text-muted">No appraisal forms uploaded yet.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-if="showModal" class="modal-backdrop" @click.self="closeModal">
      <div class="modal-panel">
        <div class="modal-header">
          <h3>{{ editingId ? 'Edit Appraisal Form' : 'Upload Appraisal Form' }}</h3>
          <button type="button" class="modal-close" @click="closeModal"><i class="bx bx-x"></i></button>
        </div>
        <div class="modal-body">
          <div class="hr-form-grid">
            <div class="hr-form-group span-2">
              <label class="hr-form-label">Form title *</label>
              <input v-model="form.title" class="hr-input" placeholder="e.g. Annual Performance Appraisal 2026" />
            </div>
            <div class="hr-form-group">
              <label class="hr-form-label">Year</label>
              <input v-model="form.appraisalYear" class="hr-input" placeholder="2026" />
            </div>
            <div class="hr-form-group">
              <label class="hr-form-label">Period label</label>
              <input v-model="form.periodLabel" class="hr-input" placeholder="Annual / Q1" />
            </div>
            <div class="hr-form-group">
              <label class="hr-form-label">Submission deadline</label>
              <input v-model="form.submissionDeadline" type="date" class="hr-input" />
            </div>
            <div class="hr-form-group">
              <label class="hr-form-label">Employee group type *</label>
              <select v-model="form.targetType" class="hr-select">
                <option value="all">All employees</option>
                <option value="department">By department</option>
                <option value="position">By position</option>
                <option value="category">By role/category</option>
              </select>
            </div>
            <div v-if="form.targetType !== 'all'" class="hr-form-group span-2">
              <label class="hr-form-label">Select groups *</label>
              <select v-model="form.targetValues" multiple class="hr-select multi-select">
                <option v-for="opt in targetOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
              </select>
              <small class="hr-text-muted">Hold Ctrl (Windows) or Cmd (Mac) to select multiple.</small>
            </div>
            <div class="hr-form-group span-2">
              <label class="hr-form-label">Description</label>
              <textarea v-model="form.description" class="hr-input" rows="3"></textarea>
            </div>
            <div class="hr-form-group span-2">
              <label class="hr-form-label">{{ editingId ? 'Replace PDF (optional)' : 'PDF form file *' }}</label>
              <input type="file" accept="application/pdf,.pdf" @change="onFileChange" class="hr-input" />
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="hr-btn hr-btn-secondary" @click="closeModal">Cancel</button>
          <button class="hr-btn hr-btn-primary" :disabled="saving" @click="saveForm">
            {{ saving ? 'Saving...' : 'Save Form' }}
          </button>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, executeDownload } from "@/api";

export default {
  name: "AppraisalFormManagement",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      saving: false,
      forms: [],
      options: { departments: [], positions: [], categories: [] },
      showModal: false,
      editingId: null,
      pdfFile: null,
      form: this.emptyForm(),
    };
  },
  computed: {
    targetOptions() {
      if (this.form.targetType === "department") {
        return (this.options.departments || []).map((d) => ({
          value: String(d.id),
          label: d.name || d.departmentName,
        }));
      }
      if (this.form.targetType === "position") {
        return (this.options.positions || []).map((p) => ({
          value: String(p.name),
          label: p.name,
        }));
      }
      if (this.form.targetType === "category") {
        return (this.options.categories || []).map((c) => ({
          value: String(c.id),
          label: c.name || c.categoryName,
        }));
      }
      return [];
    },
  },
  mounted() {
    this.loadAll();
  },
  methods: {
    emptyForm() {
      return {
        title: "",
        description: "",
        appraisalYear: String(new Date().getFullYear()),
        periodLabel: "",
        submissionDeadline: "",
        targetType: "all",
        targetValues: [],
      };
    },
    async loadAll() {
      this.loading = true;
      try {
        const optData = new FormData();
        optData.append("function", "getAppraisalPdfFormOptions");
        const optRes = await execute(optData, authUrl);
        if (optRes?.data?.success) {
          this.options = optRes.data.data || this.options;
        }

        const listData = new FormData();
        listData.append("function", "getAppraisalPdfForms");
        const listRes = await execute(listData, authUrl);
        this.forms = listRes?.data?.success ? listRes.data.data?.forms || [] : [];
      } catch (e) {
        console.error(e);
        this.$toast?.error("Failed to load appraisal forms") || alert("Failed to load appraisal forms");
      } finally {
        this.loading = false;
      }
    },
    groupLabel(form) {
      const type = form.target_type || form.targetType || "all";
      if (type === "all") return "All employees";
      const values = form.targetValues || [];
      return `${type}: ${values.length} selected`;
    },
    openCreate() {
      this.editingId = null;
      this.pdfFile = null;
      this.form = this.emptyForm();
      this.showModal = true;
    },
    openEdit(form) {
      this.editingId = form.id;
      this.pdfFile = null;
      this.form = {
        title: form.title || "",
        description: form.description || "",
        appraisalYear: form.appraisal_year || "",
        periodLabel: form.period_label || "",
        submissionDeadline: form.submission_deadline || "",
        targetType: form.target_type || "all",
        targetValues: form.targetValues || [],
      };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    onFileChange(e) {
      const file = e.target.files?.[0];
      this.pdfFile = file || null;
    },
    async saveForm() {
      if (!this.form.title.trim()) {
        this.$toast?.error("Title is required") || alert("Title is required");
        return;
      }
      if (!this.editingId && !this.pdfFile) {
        this.$toast?.error("PDF file is required") || alert("PDF file is required");
        return;
      }
      if (this.form.targetType !== "all" && (!this.form.targetValues || this.form.targetValues.length === 0)) {
        this.$toast?.error("Select at least one employee group") || alert("Select at least one employee group");
        return;
      }

      this.saving = true;
      try {
        const data = new FormData();
        data.append("function", "saveAppraisalPdfForm");
        if (this.editingId) data.append("id", this.editingId);
        data.append("title", this.form.title);
        data.append("description", this.form.description);
        data.append("appraisalYear", this.form.appraisalYear);
        data.append("periodLabel", this.form.periodLabel);
        data.append("submissionDeadline", this.form.submissionDeadline);
        data.append("targetType", this.form.targetType);
        data.append("targetValues", JSON.stringify(this.form.targetValues || []));
        if (this.pdfFile) data.append("pdfFile", this.pdfFile);

        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          this.$toast?.success(res.data.message || "Saved") || alert("Saved");
          this.closeModal();
          this.loadAll();
        } else {
          throw new Error(res?.data?.message || "Save failed");
        }
      } catch (e) {
        this.$toast?.error(e.message) || alert(e.message);
      } finally {
        this.saving = false;
      }
    },
    async deactivate(form) {
      if (!confirm(`Deactivate "${form.title}"? Employees will no longer see this form.`)) return;
      const data = new FormData();
      data.append("function", "deleteAppraisalPdfForm");
      data.append("id", form.id);
      const res = await execute(data, authUrl);
      if (res?.data?.success) {
        this.loadAll();
      }
    },
    async downloadTemplate(formId) {
      try {
        const data = new FormData();
        data.append("function", "downloadAppraisalPdfFile");
        data.append("type", "template");
        data.append("formId", formId);
        const res = await executeDownload(data, authUrl);
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");
      } catch (e) {
        this.$toast?.error("Download failed") || alert("Download failed");
      }
    },
  },
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";
.span-2 { grid-column: span 2; }
.multi-select { min-height: 120px; }
.hr-text-end { text-align: right; }
.hr-action-btns { display: flex; gap: 6px; justify-content: flex-end; flex-wrap: wrap; }
.modal-backdrop {
  position: fixed; inset: 0; background: rgba(15, 23, 42, 0.45);
  display: flex; align-items: center; justify-content: center; z-index: 2000; padding: 16px;
}
.modal-panel {
  width: min(720px, 100%); background: #fff; border-radius: 16px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.2); max-height: 90vh; overflow: auto;
}
.modal-header, .modal-footer { padding: 16px 20px; border-bottom: 1px solid #e2e8f0; display: flex; align-items: center; justify-content: space-between; }
.modal-footer { border-bottom: none; border-top: 1px solid #e2e8f0; justify-content: flex-end; gap: 10px; }
.modal-body { padding: 20px; }
.modal-close { border: none; background: transparent; font-size: 24px; cursor: pointer; }
</style>
